import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  LinearProgress,
} from '@mui/material';
import { useMemo } from 'react';
import { CustomDialogBox } from '../../../../components/CustomDialogBox';
import { generateGraphLabels, getPartialDataLabel, transformGraphLabel } from '../../../../utils/getDate';
import { getChartOptions, partialDataBackground } from '../../../../utils/chartConfigs';
import { uniqueArr } from '../../../../utils/methods';
import { BAR_GRAPH } from '../../../../utils/constants';
import FeatureUsageBody from '../../../FeatureUsage/components/FeatureUsageCard/FeatureUsageBody';
import { useChart } from '../../../../components/chart';
import { CustomNoDataOverlay } from '../../../../components/CustomNoDataOverlay';

function DialogContentElement({ loading, data }) {
  if (loading) {
    return <LinearProgress />;
  }
  if (data?.id) {
    return (
      <Accordion
        disableGutters
        expanded
        sx={{ background: 'transparent', width: '100%' }}
      >
        {data.header && (
        <AccordionSummary>
          {data.title && <Typography>{data.title}</Typography>}
          {data.header}
        </AccordionSummary>
        )}
        <AccordionDetails>
          {data.body}
        </AccordionDetails>
      </Accordion>
    );
  }
  return (
    <CustomNoDataOverlay message="No Report found for this Feature" />
  );
}

export default function KPIPreview(props) {
  const {
    open,
    handleClose,
    title = 'Feature Preview',
    previewData,
    view = 'month',
    dashboard = true,
    graphTitle = null,
    previewType = 'popup',
    loading = false,
  } = props;

  // Graph functions
  const operationCountDataset = (entityOperationCounts, viewOption) => {
    const mergedCounts = {};

    entityOperationCounts?.forEach((item) => {
      const existingCount = mergedCounts[item.type]?.count || {};
      Object.entries(item.count).forEach(([key, value]) => {
        existingCount[key] = (existingCount[key] || 0) + Number(value);
      });
      mergedCounts[item.type] = { ...item, count: existingCount };
    });

    const result = Object.values(mergedCounts);

    const dataset = result.map((operation) => {
      const opLabels = Object.keys(entityOperationCounts[0]?.count);
      const partialDateOp = getPartialDataLabel(opLabels, viewOption);

      return {
        label: operation.type,
        data: Object.values(operation.count),
        backgroundColor: opLabels.map((dateStr) => {
          return dateStr === partialDateOp
            ? BAR_GRAPH.PartialData
            : BAR_GRAPH[operation.type];
        }),
        partialDataLabel: transformGraphLabel(partialDateOp, viewOption, 'startDate'),
        barThickness: 23,
        stack: 'operation count',
      };
    });

    return dataset;
  };

  const generateDatasets = (data, chart, viewOption) => {
    const operationCounts = Object.values(data.operationCount);
    const entityOperationCounts = Object.values(data.entityOperationCount);
    const userCountData = Object.values(data.userCount);
    const operationPerUser = operationCounts.map((n, i) => (n / userCountData[i]).toFixed(0));
    const opLabels = Object.keys(data?.operationCount);
    const userLabels = Object.keys(data?.userCount);
    const partialDateOp = getPartialDataLabel(opLabels, viewOption);
    const partialDateUser = getPartialDataLabel(userLabels, viewOption);

    switch (chart) {
      case 'bar':
        return operationCountDataset(entityOperationCounts, viewOption);
      case 'combo':
        return [
          {
            label: 'Users',
            data: userCountData,
            backgroundColor: partialDataBackground(userLabels, partialDateUser),
            partialDataLabel: transformGraphLabel(partialDateUser, viewOption, 'startDate'),
            yAxisID: 'user',
            order: 2,
            barThickness: 23,
          },
          {
            label: 'Operations per User',
            data: operationPerUser,
            backgroundColor: partialDataBackground(opLabels, partialDateOp, '#FFFFFF'),
            partialDataLabel: transformGraphLabel(partialDateOp, viewOption, 'startDate'),
            borderColor: '#294E99',
            type: 'line',
            yAxisID: 'operation',
            order: 1,
            borderWidth: 2.5,
          },
        ];
      default:
        return {};
    }
  };

  const chartOptionsFn = useChart();

  const getChartData = ({ feature, config, type }) => {
    const datasets = generateDatasets(feature, config.chart, view);
    const options = getChartOptions(config);
    const chartOptions = chartOptionsFn(options);

    if (feature) {
      let keys;
      if (type === 'op') {
        const keyObj = feature?.entityOperationCount[0]?.count;
        keys = Object.keys(keyObj);
      } else {
        keys = Object.keys(feature.userCount);
        keys = uniqueArr(keys);
      }
      const labels = generateGraphLabels(keys, view, 'startDate');

      return {
        labels,
        datasets,
        chartOptions,
      };
    }
    return {};
  };

  const kpiInfo = useMemo(() => {
    if (!previewData) return {};
    const { totalActiveSeats, totalActiveAccount, featureInfo = {} } = previewData;
    const {
      featureId: id, accountCount,
    } = featureInfo;
    const userCount = featureInfo.userCount || {};
    const operationCount = featureInfo.operationCount || {};
    const userCountLen = Object.keys(userCount).length;
    const operationCountLen = Object.keys(operationCount).length;

    if (userCountLen && operationCountLen) {
      const localObj = {
        id: previewType === 'component' ? 'new' : id,
        body: (
          <FeatureUsageBody
            feature={featureInfo}
            dashboard={dashboard}
            accountCount={accountCount}
            userCountLen={userCountLen}
            operationCountLen={operationCountLen}
            totalActiveSeats={totalActiveSeats}
            totalActiveAccounts={totalActiveAccount}
            getChartData={getChartData}
            viewOption={view}
            graphTitle={graphTitle}
            preview
          />
        ),
      };

      return localObj;
    }
    const name = featureInfo.featureName;
    return name;
  });

  if (previewType === 'component') {
    return (
      <Box>
        <DialogContentElement data={kpiInfo} />
      </Box>
    );
  }

  return (
    <CustomDialogBox
      title={title}
      open={open}
      handleClose={handleClose}
      large
      content={(
        <Box sx={{ minHeight: '50vh' }}>
          <DialogContentElement loading={loading} data={kpiInfo} />
        </Box>
      )}
      actions={{ title: 'close', onClick: handleClose }}
    />
  );
}
